import { createContext, ReactNode, useContext } from 'react'
import { AddEventFormValues } from '../../TeamEventOverview/components/AddEventDialog'
import {
  CreateEventActivitiesPayload,
  UpdateEventActivityPayload,
} from 'app/frontend/base/api/event'

type TeamEventUpdateContextValue = {
  isSaving: boolean
  editEvent: (values: AddEventFormValues) => Promise<void>
  updateEvent: (values: Partial<wowaudit.Event>) => Promise<void>
  updateStatus: (
    eventParticipantId: number,
    status: wowaudit.EventParticipantReference['status'],
  ) => void
  updateSelection: (
    eventParticipantId: number,
    updates: {
      role?: string
      specId?: number | null
      characterId?: number
      selected?: boolean
    },
    activityId?: number,
  ) => void
  updateActivitySelectionForAllParticipants: (
    activityId: number,
    selected: boolean,
  ) => void
  updateEventActivity: (
    activityId: number,
    updates: UpdateEventActivityPayload,
  ) => Promise<void>
  copySelectionsFromActivity: (
    activityId: number,
    sourceActivityId: number,
    sourceEventId?: number,
  ) => Promise<void>
  exportEventActivityOverrides: (activityId: number) => Promise<void>
  updateEventActivityOrder: (activityOrder: number[]) => Promise<void>
  addEventActivities: (payload: CreateEventActivitiesPayload) => Promise<void>
  removeEventActivity: (activityId: number) => Promise<void>
  importEventFromPreviousEvent: (sourceEventId: number) => Promise<void>
  resetAllSelections: () => Promise<void>
  removeGuest: (eventParticipantId: number) => void
}

const TeamEventUpdateContext =
  createContext<TeamEventUpdateContextValue | null>(null)

type TeamEventUpdateProviderProps = {
  value: TeamEventUpdateContextValue
  children: ReactNode
}

export const TeamEventUpdateProvider = ({
  value,
  children,
}: TeamEventUpdateProviderProps) => {
  return (
    <TeamEventUpdateContext.Provider value={value}>
      {children}
    </TeamEventUpdateContext.Provider>
  )
}

export const useTeamEventUpdateContext = () => {
  const context = useContext(TeamEventUpdateContext)
  if (!context) {
    throw new Error('useTeamEventUpdateContext must be used within provider')
  }

  return context
}
